import React, { useEffect, useState } from 'react';
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import { KeyRound, TriangleAlert } from 'lucide-react';
import { BroadcastEventType } from '@cotera/api';
import { Assert } from '@cotera/utilities';
import { isInternalRowKeyDeclaration } from '@cotera/sdk/core';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { emit } from '@cotera/client/app/etc/broadcast-event-bus';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { ModalPageView } from '@cotera/client/app/components/app';
import { toast } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import {
  ResourceSelectionModel,
  ResourceType,
} from '../resources/selection/resource-selection.model';
import { DatasetCsvDropzone } from './dataset-manual-controls';
import { emitLocalDatasetImport } from './dataset-import';
import { SetDatasetRowKeysAction } from './set-dataset-row-keys.action';
import { DatasetModel } from './dataset.model';

const reuploadCsvDatasetParamsSchema = z.object({
  file: z.custom<File>(
    (value) => typeof File !== 'undefined' && value instanceof File
  ),
  datasetId: z.string().optional(),
});

type Params = z.infer<typeof reuploadCsvDatasetParamsSchema>;

type Errors = { t: string; message?: string }[];

/**
 * Whether the dataset is keyed by something a re-upload cannot renumber.
 *
 * A CSV dataset declares its row id at import, so "has a declared key" no
 * longer means "has an answer to what a row is after this file is replaced" —
 * only a key drawn from the data does.
 */
const hasBusinessRowKey = (
  rowKeyColumns: readonly string[] | null | undefined
): boolean =>
  (rowKeyColumns ?? []).length > 0 &&
  !isInternalRowKeyDeclaration(rowKeyColumns);

type ModalManager = {
  push: (props: { view: () => React.ReactNode; onClose?: () => void }) => void;
  pop: () => void;
};

type ReuploadCsvDatasetActionClient = {
  resource: {
    get: TenantedClient['resource']['get'];
    dataset: Pick<TenantedClient['resource']['dataset'], 'uploadCsvBytes'>;
    workflow: Pick<
      TenantedClient['resource']['workflow'],
      'datasetAutomations'
    >;
  };
};

/**
 * Replaces a CSV dataset's contents with a new file, in place.
 *
 * In place is the whole point: uploading the same file as a new dataset is
 * already possible and loses every trigger, binding and run attached to the old
 * one. Keeping the id keeps all of that — which is exactly why row identity has
 * to hold up first.
 *
 * A re-upload renumbers every row, so a dataset with workflows watching it and
 * no declared business key gets {@link SetDatasetRowKeysAction} first. Declining
 * that dialog cancels the re-upload rather than proceeding without a key: the
 * damage (a full re-run over every row, and orphaned run history) is silent and
 * only visible well after the fact.
 */
export class ReuploadCsvDatasetAction extends BaseAction<Params, Errors> {
  title = 'Re-upload CSV';
  description = "Replace this dataset's rows with a new CSV file";
  inputSchema = reuploadCsvDatasetParamsSchema;
  group = 'Dataset';
  icon = 'arrow-up-tray';

  constructor(
    private readonly client: ReuploadCsvDatasetActionClient,
    private readonly modalManager: ModalManager,
    private readonly orgId: string
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return (
      this.getTargetDatasetId(context) !== null && this.isCsvDataset(context)
    );
  }

  shouldAsk(): boolean {
    return true;
  }

  async execute(
    payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<Errors>> {
    const datasetId = payload.datasetId ?? this.getTargetDatasetId(context);
    if (datasetId === null || datasetId === undefined) {
      return err([{ t: 'error', message: 'No dataset in scope' }]);
    }
    // The framework never runs inputSchema, so a palette or deep-link
    // invocation can reach here with nothing picked.
    if (payload.file === undefined) {
      toast.error('No CSV file selected.');
      return err([{ t: 'error', message: 'No CSV file selected' }]);
    }

    const guard = await this.ensureRowKeys(datasetId, context);
    if (guard.isErr()) {
      return guard;
    }
    // Cancelled means the user abandoned the whole thing. Declining the key —
    // "Continue without one" — comes back ok and falls through to the upload.
    if (guard.value.cancelled === true) {
      return guard;
    }

    // No modal: the dataset page shows this import, and it outlives both this
    // request and this dialog. The upload fraction is published locally because
    // it is the one phase no server can see.
    emitLocalDatasetImport(datasetId, { phase: 'uploading', fraction: 0 });
    const result = await this.client.resource.dataset.uploadCsvBytes({
      id: datasetId,
      file: payload.file,
      onUploadProgress: (fraction) =>
        emitLocalDatasetImport(datasetId, { phase: 'uploading', fraction }),
    });
    if (result.isErr()) {
      const message =
        result.error.t === 'application'
          ? result.error.err.message ?? 'Failed to re-upload CSV'
          : 'Failed to re-upload CSV';
      // Clears the local upload state; the dataset itself never entered an
      // import, so nothing server-side will correct this.
      emitLocalDatasetImport(datasetId, { phase: 'failed', error: message });
      toast.error(message);
      return err([{ t: 'error', message }]);
    }

    // The bytes are down and the saga owns it from here, so hand the page over
    // to the server's phases rather than leaving it on a finished upload bar.
    emitLocalDatasetImport(datasetId, { phase: 'staged' });
    // Emitted locally as well as by the server so the page picks up the
    // `importing` status without waiting on the broadcast round trip.
    emit(BroadcastEventType.ResourceUpdated, {
      orgId: this.orgId,
      resourceType: 'dataset',
      resourceId: datasetId,
    });
    return ok({});
  }

  ask(params: Partial<Params>, context: ExecuteContext): AskResponse<Params> {
    const datasetId = params.datasetId ?? this.getTargetDatasetId(context);
    return {
      view: ({ onRespond, onCancel }) => {
        const handleCancel = async (): Promise<void> => {
          await onCancel();
          this.modalManager.pop();
        };
        this.modalManager.push({
          view: () => (
            <ReuploadCsvAskView
              datasetId={datasetId}
              onRespond={async (file) => {
                await onRespond({ file, datasetId: datasetId ?? undefined });
                this.modalManager.pop();
              }}
              onCancel={handleCancel}
            />
          ),
          onClose: handleCancel,
        });
      },
    };
  }

  /**
   * Makes sure this dataset can survive being renumbered, asking for a key when
   * it cannot.
   *
   * Only when something is actually watching: a dataset with no automations has
   * no identity to preserve, and demanding a key there would be a dialog with
   * no consequence behind it.
   *
   * A failure to read either the automations or the dataset is treated as "no
   * guard needed" rather than blocking the upload — the check is a safety net
   * over the user's own data, not an authorization boundary, and a transient
   * read failure should not make the dataset unwritable.
   */
  private async ensureRowKeys(
    datasetId: string,
    context: ExecuteContext
  ): Promise<ActionResult<Errors>> {
    const automations = await this.client.resource.workflow.datasetAutomations({
      datasetId,
    });
    if (automations.isErr()) {
      return ok({});
    }
    const workflowCount = automations.value.triggers.reduce(
      (total, trigger) => total + trigger.workflows.length,
      0
    );
    if (workflowCount === 0) {
      return ok({});
    }

    const resource = await this.client.resource.get({
      type: ResourceType.Dataset,
      id: datasetId,
    });
    if (resource.isErr() || resource.value.type !== 'dataset') {
      return ok({});
    }
    // A declared row id does not count. Every CSV dataset is given one at
    // import, so treating "has a key" as "is safe to re-upload" would silence
    // this prompt for exactly the datasets it exists for.
    if (hasBusinessRowKey(resource.value.dataset.rowKeyColumns)) {
      return ok({});
    }

    const setRowKeys = context.getAction(SetDatasetRowKeysAction);
    if (setRowKeys === undefined || setRowKeys === null) {
      return ok({});
    }
    // Why this is happening was already said in the ask view, next to the file
    // picker and before anything was chosen. Repeating it here would be a
    // notification about a dialog that is opening in the same breath.
    // `allowSkip` gives that dialog a way out that is not cancelling: the user
    // is told what going without a key costs, and gets to decide anyway.
    return await setRowKeys.askOrExecute(
      { datasetId, allowSkip: true },
      context
    );
  }

  private selectedDataset(
    context: ApplicableContext
  ): ResourceSelectionModel | null {
    if (!context.isInScope(ResourceSelectionModel)) {
      return null;
    }
    const selection = context.getInScopeModelsOfType(ResourceSelectionModel);
    const single = selection.length === 1 ? selection[0] : undefined;
    if (
      single === undefined ||
      single.selections.length !== 1 ||
      single.getResourceIdsForType(ResourceType.Dataset).length !== 1
    ) {
      return null;
    }
    return single;
  }

  private getTargetDatasetId(context: ApplicableContext): string | null {
    const selected = this.selectedDataset(context);
    if (selected !== null) {
      return selected.getResourceIdsForType(ResourceType.Dataset)[0] ?? null;
    }
    const datasetModel = context.targetedModelOfType(DatasetModel);
    return datasetModel?.datasetId ?? null;
  }

  /**
   * Only the CSV kind. Manual datasets have their own import path with its own
   * row-id strategy, and the endpoint behind this action rejects them.
   */
  private isCsvDataset(context: ApplicableContext): boolean {
    const datasetModel = context.targetedModelOfType(DatasetModel);
    const dataset =
      datasetModel?.datasetDataWatchable.snapshot()?.resource?.dataset;
    return dataset?.type === 'csv';
  }
}

/**
 * What re-uploading would do to row identity, as far as the dialog can tell.
 *
 * `unwatched` and `keyed` are both fine outcomes but for different reasons, and
 * only `unkeyed` changes what the user is about to be asked to do — which is
 * why it is resolved before the file picker rather than announced after it.
 */
type RowIdentityState =
  | { t: 'loading' }
  | { t: 'unknown' }
  | { t: 'unwatched' }
  | { t: 'keyed'; columns: string[] }
  | { t: 'unkeyed'; workflowCount: number };

const ReuploadCsvAskView: React.FC<{
  datasetId: string | null;
  onRespond: (file: File) => Promise<void>;
  onCancel: () => Promise<void>;
}> = ({ datasetId, onRespond, onCancel }) => {
  const client = useTenantedClient();
  const [busy, setBusy] = useState(false);
  const [identity, setIdentity] = useState<RowIdentityState>({ t: 'loading' });

  useEffect(() => {
    if (datasetId === null) {
      setIdentity({ t: 'unknown' });
      return;
    }
    let cancelled = false;
    void (async () => {
      // The same two reads `ensureRowKeys` makes. Done again rather than
      // threaded through, because the dialog opens before execute runs and the
      // answer can change in between — a workflow bound in another tab should
      // reach this warning.
      const [automations, resource] = await Promise.all([
        client.resource.workflow.datasetAutomations({ datasetId }),
        client.resource.get({ type: ResourceType.Dataset, id: datasetId }),
      ]);
      if (cancelled) {
        return;
      }
      if (
        automations.isErr() ||
        resource.isErr() ||
        resource.value.type !== 'dataset'
      ) {
        setIdentity({ t: 'unknown' });
        return;
      }
      const workflowCount = automations.value.triggers.reduce(
        (total, trigger) => total + trigger.workflows.length,
        0
      );
      const columns = resource.value.dataset.rowKeyColumns ?? [];
      if (workflowCount === 0) {
        setIdentity({ t: 'unwatched' });
        return;
      }
      setIdentity(
        hasBusinessRowKey(columns)
          ? { t: 'keyed', columns: [...columns] }
          : { t: 'unkeyed', workflowCount }
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [client, datasetId]);

  return (
    <ModalPageView
      title="Re-upload CSV"
      subtitle="Replace every row in this dataset with a new file"
      className="px-4 py-4"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            The new file replaces the dataset's schema and all of its rows.
            Unsaved edits to the current rows are discarded. Triggers, workflows
            and sharing stay attached.
          </span>
        </div>
        <RowIdentityNotice identity={identity} />
        <DatasetCsvDropzone
          disabled={busy}
          onSelect={async (file) => {
            setBusy(true);
            try {
              await onRespond(file);
            } finally {
              setBusy(false);
            }
          }}
        />
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => void onCancel()}
            disabled={busy}
          >
            Cancel
          </Button>
        </div>
      </div>
    </ModalPageView>
  );
};

/**
 * Says what a re-upload does to row identity, before a file is chosen.
 *
 * Deliberately in the dialog rather than a toast: it is a reason to make a
 * different choice, so it belongs next to the choice — and the `unkeyed` case
 * tells the user about a second dialog they are about to be shown, which a
 * notification firing at the same moment cannot do.
 */
const RowIdentityNotice: React.FC<{ identity: RowIdentityState }> = ({
  identity,
}) => {
  switch (identity.t) {
    // Nothing worth saying: no automation depends on identity here, or the
    // lookup failed and a guess would be worse than silence.
    case 'loading':
    case 'unknown':
    case 'unwatched':
      return null;
    case 'keyed':
      return (
        <div className="flex items-start gap-2 rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          <KeyRound className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Rows are identified by{' '}
            <span className="font-medium text-foreground">
              {identity.columns.join(', ')}
            </span>
            , so workflows will match them up across this upload.
          </span>
        </div>
      );
    case 'unkeyed':
      return (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {identity.workflowCount === 1
              ? 'A workflow watches this dataset'
              : `${identity.workflowCount} workflows watch this dataset`}{' '}
            and it has no row identifier. Re-uploading renumbers every row, so
            they would all look new and run again, and the runs already recorded
            would stop lining up. You'll be asked to pick identifier columns
            before the upload starts.
          </span>
        </div>
      );
    default:
      return Assert.unreachable(identity);
  }
};
